import { getDailyMovieId, getInfiniteMovieId, getResultGrid, getCurrentDay, preloadImage, updateUserStats } from './utils/gameUtils';
import React, { useState, useEffect, useCallback, useRef, createContext, useContext } from 'react';
/* Fix: Import from react-router instead of react-router-dom for v7 unified package support */
import { BrowserRouter, Routes, Route, useNavigate, useParams, Link, useLocation, Navigate } from 'react-router';
import { Movie, HydratedMovie, GameState, SupportedLang } from './types';
import { TRANSLATIONS, FALLBACK_LANG, SUPPORTED_LANGS, TMDB_IMAGE_BASE, TMDB_POSTER_BASE, EMERGENCY_MOVIE, SLUGS } from './constants';
import FrameDisplay from './components/FrameDisplay';
import MovieSearch from './components/MovieSearch';
import GuessList from './components/GuessList';
import Header from './components/Header';
import Home from './components/Home';
import Archive from './components/Archive';
import { InfoPages } from './components/InfoPages';
// Fix: Added missing Clock import from lucide-react
import { ChevronLeft, HelpCircle, Clock } from 'lucide-react';

// "The Shield" - Local Session Cache
const movieDataCache: Record<string, any> = {};

interface HistoryContextType {
  navigationStack: string[];
  setNavigationStack: (stack: string[]) => void;
}

const HistoryContext = createContext<HistoryContextType | null>(null);

export const useSmartHistory = () => {
  const context = useContext(HistoryContext);
  if (!context) throw new Error("useSmartHistory must be used within a HistoryProvider");
  return context;
};

export const useSmartBack = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { navigationStack, setNavigationStack } = useSmartHistory();

  const pathParts = location.pathname.split('/').filter(Boolean);
  const lang = (SUPPORTED_LANGS.includes(pathParts[0] as SupportedLang) && pathParts[0] !== 'en')
    ? (pathParts[0] as SupportedLang)
    : FALLBACK_LANG;
  const prefix = lang === 'en' ? '' : `/${lang}`;

  const getPageKey = (pathname: string) => {
    const parts = pathname.split('/').filter(Boolean);
    if (parts.length === 0) return 'home';
    const l = SUPPORTED_LANGS.includes(parts[0] as any) ? (parts[0] as SupportedLang) : 'en';
    const slugPart = l === 'en' ? parts[0] : parts[1];
    if (!slugPart) return 'home';
    const key = Object.keys(SLUGS[l]).find(k => SLUGS[l][k] === slugPart);
    return key || slugPart;
  };

  const handleBack = () => {
    const currentKey = getPageKey(location.pathname);
    let newStack = [...navigationStack];
    if (newStack.length > 0 && newStack[newStack.length - 1] === currentKey) {
      newStack.pop();
    }
    if (newStack.length > 0) {
      const prevPageKey = newStack[newStack.length - 1];
      setNavigationStack(newStack);
      const slug = SLUGS[lang][prevPageKey] !== undefined ? SLUGS[lang][prevPageKey] : prevPageKey;
      const path = (prevPageKey === 'home') ? (prefix || '/') : `${prefix}/${slug}`.replace(/\/+$/, '');
      navigate(path);
    } else {
      navigate(prefix || '/');
    }
  };

  const handleGoHome = () => {
    navigate(prefix || '/');
  };

  return { handleBack, handleGoHome, lang, prefix, pathParts };
};

export const useLanguageNavigate = () => {
  const { lang, prefix } = useSmartBack();
  const navigate = useNavigate();

  return (key: string, params: string = '', subParam: string = '') => {
    if (key === 'home') {
      navigate(prefix || '/');
      return;
    }
    const slug = SLUGS[lang][key] !== undefined ? SLUGS[lang][key] : key;
    const cleanSlug = slug === '' ? '' : `/${slug}`;
    const mid = params ? `/${params}` : '';
    const end = subParam ? `/${subParam}` : '';
    const finalPath = `${prefix}${cleanSlug}${mid}${end}` || '/';
    navigate(finalPath);
  };
};

const hideLoadingOverlay = () => {
  const loadingOverlay = document.getElementById('loading-overlay');
  if (loadingOverlay && loadingOverlay.style.display !== 'none') {
    loadingOverlay.style.opacity = '0';
    setTimeout(() => { 
      if (loadingOverlay) loadingOverlay.style.display = 'none'; 
    }, 600);
  }
};

const FutureLockedScreen: React.FC<{ day: number, lang: SupportedLang }> = ({ day, lang }) => {
  const langNavigate = useLanguageNavigate();
  const title = TRANSLATIONS.futureTitle[lang];
  const description = TRANSLATIONS.futureDesc[lang].replace('#{id}', day.toString());
  const homeLabel = TRANSLATIONS.backToHomeLabel[lang];

  return (
    <div className="flex-1 flex flex-col items-center justify-center p-10 text-center space-y-8 animate-fade-in">
      <div className="w-24 h-24 bg-zinc-100 dark:bg-zinc-900 rounded-full flex items-center justify-center border border-zinc-200 dark:border-zinc-800">
        <Clock className="w-10 h-10 text-prestige-gold" />
      </div>
      <div className="space-y-4">
        <h2 className="text-3xl font-heading text-ink dark:text-off-white uppercase tracking-widest">
          {title}
        </h2>
        <p className="text-zinc-500 max-w-sm mx-auto font-medium leading-relaxed">
          {description}
        </p>
      </div>
      <button 
        onClick={() => langNavigate('home')} 
        className="bg-prestige-gold hover:bg-amber-500 text-midnight px-10 py-4 rounded-2xl font-bold uppercase text-xs tracking-[0.2em] shadow-xl shadow-prestige-gold/20 transition-all hover:-translate-y-1 active:scale-95"
      >
        {homeLabel}
      </button>
    </div>
  );
};

const CinematicToast: React.FC<{ message: string; isVisible: boolean }> = ({ message, isVisible }) => {
  return (
    <div className={`fixed bottom-10 left-1/2 -translate-x-1/2 z-[300] transition-all duration-300 pointer-events-none ${isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-4 scale-95'}`}>
      <div className="bg-midnight/80 dark:bg-emerald/90 backdrop-blur-md px-6 py-3 rounded-full border border-white/10 shadow-2xl">
        <span className="text-white font-black uppercase text-[10px] tracking-[0.2em] whitespace-nowrap">
          {message}
        </span>
      </div>
    </div>
  );
};

const ResultModal: React.FC<{
  isOpen: boolean; onClose: () => void; gameState: GameState; movie: Movie; lang: SupportedLang; onShare: () => void; showShareToast: boolean;
  mode: 'daily' | 'infinite'; onNext?: () => void;
}> = ({ isOpen, onClose, gameState, movie, lang, onShare, showShareToast, mode, onNext }) => {
  const langNavigate = useLanguageNavigate();
  if (!isOpen) return null;
  const headerText = mode === 'daily' ? TRANSLATIONS.gameResult[lang] : TRANSLATIONS.reelRevealed[lang];
  const ctaText = mode === 'daily' ? TRANSLATIONS.retentionCTA[lang] : TRANSLATIONS.nextReel[lang];

  return (
    <>
      <div className="fixed inset-0 z-[200] flex items-center justify-center p-6 bg-midnight/80 animate-fade-in backdrop-blur-md">
        <div className="bg-[#1F2937] w-full max-w-[440px] rounded-[28px] overflow-hidden shadow-2xl relative animate-scale-in border border-white/5 flex flex-col">
          <button onClick={onClose} className="absolute top-4 right-4 z-20 p-2 bg-black/40 hover:bg-black/60 text-white/80 hover:text-white rounded-full transition-all border border-white/10 backdrop-blur-sm">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
          <div className="relative aspect-video w-full overflow-hidden shrink-0">
            <img src={`${TMDB_IMAGE_BASE}${movie.backdrop_path}`} alt={movie.title} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1F2937] via-transparent to-black/30" />
          </div>
          <div className="p-8 flex flex-col items-center text-center -mt-6 relative z-10">
            {gameState.status === 'lost' && (
              <div className="mb-4 px-5 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[0.25em] shadow-lg bg-crimson text-white shadow-crimson/20">
                {TRANSLATIONS.failBadge[lang]}
              </div>
            )}
            <h2 className={`font-heading tracking-wide leading-[0.95] text-white uppercase mb-6 px-2 ${gameState.status === 'won' ? 'text-[40px] mt-2' : 'text-4xl'}`}>
              {movie.title}
            </h2>
            <div className="space-y-3 w-full mb-10">
              <span className="text-zinc-500 font-bold uppercase tracking-[0.2em] text-[10px]">
                {headerText}
              </span>
              <div className="flex justify-center items-center gap-2.5 text-2xl filter drop-shadow-md">
                {gameState.guesses.map((g, i) => (
                  <span key={i} className="transition-transform hover:scale-110">
                    {g.toLowerCase() === movie.title.toLowerCase() ? '🟩' : '🟥'}
                  </span>
                ))}
                {Array.from({ length: 6 - gameState.guesses.length }).map((_, i) => (
                  <span key={i} className="opacity-20 grayscale brightness-50">⬛</span>
                ))}
              </div>
            </div>
            <div className="w-full space-y-3.5">
              <div className="relative w-full">
                <button onClick={onShare} className="w-full bg-emerald hover:bg-[#0EA271] text-white font-bold py-4 rounded-2xl flex items-center justify-center gap-3 shadow-xl shadow-emerald/30 active:scale-[0.98] transition-all uppercase tracking-[0.15em] text-xs">
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5} 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z"/></svg>
                  {TRANSLATIONS.share[lang]}
                </button>
              </div>
              <button onClick={() => {
                  if (mode === 'daily') {
                    onClose();
                    langNavigate('infinite');
                  } else if (onNext) {
                    onNext();
                  }
                }} className={`w-full font-heading tracking-[0.15em] py-4 rounded-2xl text-base uppercase transition-all duration-300 flex items-center justify-center text-center ${
                  mode === 'infinite' ? 'bg-transparent border border-white text-white hover:bg-white hover:text-midnight' : 'bg-transparent border border-white/10 hover:bg-white/5 text-white/80 hover:text-white'
                }`}>
                {ctaText}
              </button>
            </div>
          </div>
        </div>
      </div>
      <CinematicToast message={TRANSLATIONS.copied[lang]} isVisible={showShareToast} />
    </>
  );
};

const SolutionPage: React.FC<{ lang: SupportedLang }> = ({ lang }) => {
  const { subSlug } = useParams<{ subSlug: string }>();
  const [movie, setMovie] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLocked, setIsLocked] = useState(false);
  const langNavigate = useLanguageNavigate();

  const requestedDay = parseInt(subSlug || '0');
  const today = getCurrentDay() + 1;
  const isToday = requestedDay === today;

  useEffect(() => {
    const load = async () => {
      if (!subSlug) return;

      if (requestedDay > today) {
        setIsLocked(true);
        setIsLoading(false);
        hideLoadingOverlay();
        return;
      }

      setIsLoading(true);
      try {
        const response = await fetch(`/api/daily-movie?lang=${lang}&day=${subSlug}`);
        const data = await response.json();
        if (data.error === 'FUTURE_REEL_LOCKED') {
          setIsLocked(true);
        } else {
          setMovie(data);
          document.title = `${TRANSLATIONS.movieRiddle[lang]} ${TRANSLATIONS.dayLabel[lang]} #${subSlug} - Solution`;
        }
      } catch (e) { 
        console.error(e); 
      } finally { 
        setIsLoading(false); 
        hideLoadingOverlay(); 
      }
    };
    load();
  }, [subSlug, lang, requestedDay, today]);

  if (isLoading) return <div className="flex-1 flex items-center justify-center">...</div>;
  if (isLocked) return <FutureLockedScreen day={requestedDay} lang={lang} />;
  if (!movie) return <div className="flex-1 flex items-center justify-center">Error loading solution</div>;

  const getPosterUrl = () => {
    if (movie.poster_path) return `${TMDB_POSTER_BASE}${movie.poster_path}`;
    if (movie.backdrop_path) return `${TMDB_POSTER_BASE}${movie.backdrop_path}`;
    if (movie.images?.[0]) return `${TMDB_POSTER_BASE}${movie.images[0]}`;
    return "https://via.placeholder.com/500x750?text=No+Poster";
  };

  return (
    <div className="w-full max-w-2xl mx-auto py-12 px-6 animate-fade-in flex flex-col items-center gap-10">
      <h1 className="text-4xl sm:text-5xl font-heading text-center tracking-widest uppercase text-ink dark:text-off-white">
        {TRANSLATIONS.movieRiddle[lang]} {TRANSLATIONS.dayLabel[lang]} #{subSlug}<br/>
        <span className="text-prestige-gold text-2xl sm:text-3xl">{TRANSLATIONS.solutionAnswerTitle[lang]}</span>
      </h1>
      <div className="w-full bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-3xl overflow-hidden shadow-2xl flex flex-col sm:flex-row gap-6 p-6 sm:p-10">
        <div className="w-full sm:w-1/2 shrink-0 aspect-[2/3] rounded-2xl overflow-hidden shadow-lg border border-black/5 bg-zinc-100 dark:bg-zinc-800">
          <img src={getPosterUrl()} alt={movie.title} className="w-full h-full object-cover" />
        </div>
        <div className="flex flex-col justify-center gap-4 text-center sm:text-left">
          <h2 className="text-3xl sm:text-4xl font-heading text-ink dark:text-off-white leading-tight">{movie.title}</h2>
          <span className="bg-prestige-gold text-midnight px-3 py-1 rounded-md text-sm font-black w-fit mx-auto sm:mx-0">
            {movie.release_date?.split('-')[0]}
          </span>
          <p className="text-sm text-zinc-500 dark:text-zinc-400 line-clamp-6 leading-relaxed">{movie.overview}</p>
        </div>
      </div>
      <div className="w-full flex flex-col items-center gap-4 pt-10">
        {!isToday && (
          <h3 className="text-zinc-400 font-bold uppercase tracking-[0.2em] text-xs">
            {TRANSLATIONS.playChallengeNow[lang]}
          </h3>
        )}
        <button 
          onClick={() => langNavigate(isToday ? 'infinite' : 'daily')} 
          className="bg-emerald text-white px-12 py-5 rounded-2xl font-heading tracking-widest text-xl shadow-xl shadow-emerald/20 hover:scale-105 transition-transform uppercase"
        >
          {isToday ? TRANSLATIONS.tryPracticeMode[lang] : TRANSLATIONS.playTodaysChallenge[lang]}
        </button>
      </div>
    </div>
  );
};

const CinemaFrameGame: React.FC<{ mode: 'daily' | 'infinite', lang: SupportedLang }> = ({ mode, lang }) => {
  const { subSlug } = useParams<{ subSlug?: string }>();
  const langNavigate = useLanguageNavigate();

  const todayIndex = getCurrentDay();
  const [isLocked, setIsLocked] = useState(false);
  const internalDayIndex = subSlug ? Math.max(0, parseInt(subSlug) - 1) : todayIndex;
  
  const [movie, setMovie] = useState<Movie | null>(null);
  const [images, setImages] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [gameState, setGameState] = useState<GameState>({ 
    guesses: [], 
    status: 'playing', 
    day: internalDayIndex, 
    lastPlayed: new Date().toISOString() 
  });
  const [message, setMessage] = useState('');
  const [showShareToast, setShowShareToast] = useState(false);
  const [isResultModalOpen, setIsResultModalOpen] = useState(false);
  
  const playedIdsRef = useRef<Set<number>>(new Set());
  const messageTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const shareTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearToast = useCallback(() => {
    if (messageTimeoutRef.current) clearTimeout(messageTimeoutRef.current);
    setMessage('');
  }, []);

  const showToast = useCallback((msg: string, duration = 3000) => {
    if (messageTimeoutRef.current) clearTimeout(messageTimeoutRef.current);
    setMessage(msg);
    messageTimeoutRef.current = setTimeout(() => setMessage(''), duration);
  }, []);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (messageTimeoutRef.current) clearTimeout(messageTimeoutRef.current);
      if (shareTimeoutRef.current) clearTimeout(shareTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (mode === 'daily') {
      const displayId = subSlug ? subSlug : (todayIndex + 1);
      document.title = `Movie Riddle #${displayId} - Daily Challenge`;
    }
  }, [mode, todayIndex, subSlug]);

  const fetchMovieData = async (movieId: number) => {
    const cacheKey = `${movieId}-${lang}`;
    if (movieDataCache[cacheKey]) return movieDataCache[cacheKey];
    const res = await fetch(`/api/movie?id=${movieId}&lang=${lang}`);
    const data = await res.json();
    movieDataCache[cacheKey] = data;
    return data;
  };

  const initGame = useCallback(async () => { 
    setIsLoading(true); 
    setError(null); 
    setIsLocked(false);
    setIsResultModalOpen(false);
    clearToast(); 
    
    try {
      let movieData: any = null;

      if (mode === 'daily') {
        const requestedDay = subSlug ? parseInt(subSlug) : todayIndex + 1;
        const currentDay = todayIndex + 1;

        if (requestedDay > currentDay) {
          setIsLocked(true);
          setIsLoading(false);
          hideLoadingOverlay();
          return;
        }

        const response = await fetch(`/api/daily-movie?lang=${lang}&day=${requestedDay}`);
        movieData = await response.json();
        if (movieData.error === 'FUTURE_REEL_LOCKED') {
          setIsLocked(true);
          return;
        }
      } else {
        const response = await fetch('/movies.json');
        const catalog = await response.json();
        const movieId = getInfiniteMovieId(catalog, playedIdsRef.current);
        if (!movieId) {
          const trendingRes = await fetch(`/api/daily-movie?lang=${lang}&day=${Math.floor(Math.random() * 1000)}`);
          movieData = await trendingRes.json();
        } else {
          movieData = await fetchMovieData(movieId);
          playedIdsRef.current.add(movieId);
        }
      }

      setMovie({
        id: movieData.id,
        title: movieData.title,
        backdrop_path: movieData.images[movieData.images.length - 1],
        poster_path: movieData.poster_path || '',
        release_date: movieData.release_date
      });
      setImages(movieData.images);

      if (mode === 'daily') {
        const saved = localStorage.getItem(`MovieRiddle_Daily_Index_${internalDayIndex}`);
        if (saved) { 
          const parsed = JSON.parse(saved); 
          setGameState(parsed); 
          if (parsed.status !== 'playing') setIsResultModalOpen(true); 
        } else {
          setGameState({ guesses: [], status: 'playing', day: internalDayIndex, lastPlayed: new Date().toISOString() });
        }
      } else {
        setGameState({ guesses: [], status: 'playing', day: -1, lastPlayed: new Date().toISOString() });
      }
    } catch (err: any) { 
      setError(err.message);
    } finally { 
      setIsLoading(false); 
      hideLoadingOverlay(); 
    }
  }, [internalDayIndex, lang, mode, subSlug, todayIndex, clearToast]);

  useEffect(() => { initGame(); }, [initGame]);

  const handleGuess = (guessMovie: Movie) => {
    if (gameState.status !== 'playing' || !movie) return;
    const isCorrect = guessMovie.id === movie.id || guessMovie.title.toLowerCase() === movie.title.toLowerCase();
    const newGuesses = [...gameState.guesses, guessMovie.title];
    const newStatus: GameState['status'] = isCorrect ? 'won' : newGuesses.length === 6 ? 'lost' : 'playing';
    
    if (isCorrect) showToast(TRANSLATIONS.winMessage[lang]);
    else if (newStatus === 'lost') showToast(`${TRANSLATIONS.loseMessage[lang]} ${movie.title}`, 5000);
    else showToast(TRANSLATIONS.nextFrame[lang], 2000);
    
    const newState = { ...gameState, guesses: newGuesses, status: newStatus };
    setGameState(newState);
    if (mode === 'daily') localStorage.setItem(`MovieRiddle_Daily_Index_${internalDayIndex}`, JSON.stringify(newState));
    if (newStatus !== 'playing') {
      updateUserStats(isCorrect, newGuesses.length, mode === 'daily', internalDayIndex === todayIndex);
      setTimeout(() => setIsResultModalOpen(true), 1500);
    }
  };

  const handleSkip = () => {
    if (gameState.status !== 'playing' || !movie) return;
    const newGuesses = [...gameState.guesses, TRANSLATIONS.skipped[lang]];
    const newStatus: GameState['status'] = newGuesses.length === 6 ? 'lost' : 'playing';
    if (newStatus === 'lost') showToast(`${TRANSLATIONS.loseMessage[lang]} ${movie.title}`, 5000);
    else showToast(TRANSLATIONS.nextFrame[lang], 2000);
    const newState = { ...gameState, guesses: newGuesses, status: newStatus };
    setGameState(newState);
    if (newStatus !== 'playing') {
      updateUserStats(false, newGuesses.length, false, false);
      setTimeout(() => setIsResultModalOpen(true), 1500);
    }
  };

  const handleShare = () => {
    if (!movie) return;
    const dayValue = mode === 'daily' ? (internalDayIndex + 1) : TRANSLATIONS.infiniteMode[lang];
    const gridText = getResultGrid(gameState.guesses, movie.title, dayValue, lang, gameState.status === 'won');
    navigator.clipboard.writeText(gridText);
    
    setShowShareToast(true); 
    if (shareTimeoutRef.current) clearTimeout(shareTimeoutRef.current);
    shareTimeoutRef.current = setTimeout(() => setShowShareToast(false), 1500);
  };

  if (isLoading) return <div className="flex-1 flex items-center justify-center">...</div>;
  if (isLocked) return <FutureLockedScreen day={parseInt(subSlug || (todayIndex + 1).toString())} lang={lang} />;
  if (error) return <div className="flex-1 flex flex-col items-center justify-center p-10 text-center space-y-4">
    <h2 className="text-2xl font-heading text-red-500 uppercase tracking-widest">ACCESS DENIED</h2>
    <p className="text-zinc-500 max-w-sm">{error}</p>
    <button onClick={() => langNavigate('home')} className="bg-ink dark:bg-off-white text-off-white dark:text-ink px-8 py-3 rounded-xl font-bold uppercase text-xs tracking-widest">Return Home</button>
  </div>;

  return (
    <div className="w-full max-w-2xl lg:max-w-[700px] mx-auto flex-1 flex flex-col items-center gap-3 lg:gap-3 animate-fade-in py-2 lg:py-4 px-4">
      {subSlug && (
        <div className="w-full flex justify-start mb-0">
          <button onClick={() => langNavigate('archive')} className="text-zinc-500 hover:text-ink dark:hover:text-off-white transition-colors flex items-center gap-2 font-bold uppercase text-[9px] tracking-widest bg-zinc-100 dark:bg-surface/50 px-3 py-1.5 rounded-lg border border-zinc-200 dark:border-zinc-800/50">
            <ChevronLeft className="w-3.5 h-3.5" strokeWidth={3} />
            {TRANSLATIONS.backLabel[lang]} TO {TRANSLATIONS.archive[lang].toUpperCase()}
          </button>
        </div>
      )}
      <div className="relative w-full">
        <FrameDisplay images={images} currentAttempt={gameState.guesses.length} lang={lang} />
        <div className={`fixed top-24 left-1/2 -translate-x-1/2 z-[100] transition-all duration-500 transform ${message ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 -translate-y-4 scale-95 pointer-events-none'}`}>
          <div className="bg-ink/90 dark:bg-off-white/95 text-off-white dark:text-ink px-8 py-3 rounded-full font-bold shadow-2xl text-[11px] uppercase tracking-widest whitespace-nowrap border border-white/10 dark:border-black/5 backdrop-blur-md">
            {message}
          </div>
        </div>
      </div>
      <GuessList guesses={gameState.guesses} correctTitle={movie?.title || ''} />
      {mode === 'daily' && gameState.guesses.length === 5 && gameState.status === 'playing' && (
        <button 
          onClick={() => langNavigate('day', (internalDayIndex + 1).toString(), SLUGS[lang].solution)} 
          className="text-xs font-bold uppercase tracking-widest text-zinc-500 hover:text-zinc-400 transition-colors flex items-center gap-2 mt-1"
        >
          <HelpCircle className="w-3 h-3" />
          {TRANSLATIONS.stuckLabel[lang]}
        </button>
      )}
      <div className="w-full mt-2">
        {gameState.status === 'playing' ? <MovieSearch onGuess={handleGuess} onSkip={handleSkip} showSkip={mode === 'infinite'} lang={lang} disabled={false} /> : (
          <div className="flex flex-col gap-3">
             <button onClick={handleShare} className="w-full bg-emerald text-white font-bold py-3.5 rounded-xl text-xs uppercase tracking-[0.2em] shadow-lg shadow-emerald/20">{TRANSLATIONS.share[lang]}</button>
             {mode === 'infinite' && <button onClick={initGame} className="w-full bg-ink dark:bg-off-white text-off-white dark:text-ink font-heading tracking-widest py-3.5 rounded-xl text-lg uppercase">{TRANSLATIONS.nextReel[lang]}</button>}
          </div>
        )}
      </div>
      {movie && <ResultModal isOpen={isResultModalOpen} onClose={() => setIsResultModalOpen(false)} gameState={gameState} movie={movie} lang={lang} onShare={handleShare} showShareToast={showShareToast} mode={mode} onNext={mode === 'infinite' ? initGame : undefined} />}
      <CinematicToast message={TRANSLATIONS.copied[lang]} isVisible={showShareToast && !isResultModalOpen} />
    </div>
  );
};

const Footer: React.FC = () => {
  const location = useLocation();
  const pathParts = location.pathname.split('/').filter(Boolean);
  const lang = (SUPPORTED_LANGS.includes(pathParts[0] as SupportedLang) && pathParts[0] !== 'en')
    ? (pathParts[0] as SupportedLang)
    : FALLBACK_LANG;
  const prefix = lang === 'en' ? '' : `/${lang}`;

  const footerLinks = ['archive', 'about', 'guide', 'privacy', 'terms', 'contact'];

  return (
    <footer className="border-t border-zinc-200 dark:border-zinc-800 flex flex-col items-center justify-center px-4 py-10 bg-off-white dark:bg-midnight shrink-0 gap-y-4">
      <nav className="flex flex-wrap justify-center items-center gap-x-4 sm:gap-x-8 gap-y-2">
        {footerLinks.map(key => (
          <Link 
            key={key} 
            to={`${prefix}/${SLUGS[lang][key]}`.replace(/\/+$/, '') || '/'} 
            className="text-[10px] sm:text-[11px] font-bold uppercase tracking-widest text-zinc-400 dark:text-zinc-400 hover:text-prestige-gold transition-colors"
          >
            {TRANSLATIONS[key][lang]}
          </Link>
        ))}
      </nav>
      <div className="text-[9px] sm:text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-500 flex items-center justify-center">
        <span>© 2026 Movie Riddle</span>
        <span className="mx-2 text-prestige-gold scale-150 leading-none select-none">•</span>
        <span>{TRANSLATIONS.footerTagline[lang]}</span>
      </div>
    </footer>
  );
};

const App: React.FC = () => {
  const [navigationStack, setNavigationStack] = useState<string[]>([]);

  useEffect(() => {
    hideLoadingOverlay();
  }, []);

  return (
    <HistoryContext.Provider value={{ navigationStack, setNavigationStack }}>
      <BrowserRouter>
        <div className="min-h-screen bg-off-white dark:bg-midnight transition-colors duration-500 flex flex-col font-sans">
          <Header />
          <main className="flex-1 flex flex-col relative">
            <Routes>
              {/* Home / Language Root */}
              <Route path="/" element={<Home />} />
              <Route path="/:lang" element={<Home />} />

              {/* Static Pages */}
              {SUPPORTED_LANGS.map(l => (
                <React.Fragment key={l}>
                  <Route path={`${l === 'en' ? '' : '/' + l}/${SLUGS[l].about}`} element={<InfoPages type="about" />} />
                  <Route path={`${l === 'en' ? '' : '/' + l}/${SLUGS[l].guide}`} element={<InfoPages type="guide" />} />
                  <Route path={`${l === 'en' ? '' : '/' + l}/${SLUGS[l].privacy}`} element={<InfoPages type="privacy" />} />
                  <Route path={`${l === 'en' ? '' : '/' + l}/${SLUGS[l].terms}`} element={<InfoPages type="terms" />} />
                  <Route path={`${l === 'en' ? '' : '/' + l}/${SLUGS[l].contact}`} element={<InfoPages type="contact" />} />
                  <Route path={`${l === 'en' ? '' : '/' + l}/${SLUGS[l].archive}`} element={<Archive />} />
                  
                  {/* Game Modes */}
                  <Route path={`${l === 'en' ? '' : '/' + l}/${SLUGS[l].daily}`} element={<CinemaFrameGame mode="daily" lang={l} />} />
                  <Route path={`${l === 'en' ? '' : '/' + l}/${SLUGS[l].infinite}`} element={<CinemaFrameGame mode="infinite" lang={l} />} />
                  
                  {/* Archived / Specific Day */}
                  <Route path={`${l === 'en' ? '' : '/' + l}/${SLUGS[l].day}/:subSlug`} element={<CinemaFrameGame mode="daily" lang={l} />} />
                  
                  {/* Solution View */}
                  <Route path={`${l === 'en' ? '' : '/' + l}/${SLUGS[l].day}/:subSlug/${SLUGS[l].solution}`} element={<SolutionPage lang={l} />} />
                </React.Fragment>
              ))}

              {/* Fallback */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
          <Footer />
        </div>
      </BrowserRouter>
    </HistoryContext.Provider>
  );
};

export default App;